import { fetchAll, src } from "./assets.js";
import { beaconAssets, beaconPack } from "./beacon-pack.js";

// Beacon: a phone or a tablet as a physical game objective. Two modes — a
// charge to arm and defend, and a control point to fight over — and one
// interaction between them: press and hold.
//
// The design rule this whole file serves: **make one-button interaction look
// and feel much more complex than it is.** Everything ornate here is
// presentation — scrambling combinations, false attempts, corrupted terminal
// output, a keypad that lights up under a thumb that never touches it. The
// model underneath is six states and a timestamp, and it stays legible while a
// ten-year-old is standing on it in a garden.
//
// It shares three things with the event app and nothing else: the preload gate
// (`assets.js`), the offline posture — media in memory, state on disk, no
// transport — and fullscreen. It imports no gsap, no Supabase, no Games.

/* ---------- the model ---------- */

// The four durations the game is made of. Constants rather than settings: the
// spec names them, and a number nobody has wanted to change yet is not a
// feature.
// ponytail: no settings sheet. `defend` is the one a real field will argue
// with — a big garden wants 90s. Make these a sheet the first time somebody
// asks, not before.
// The fuses a host can pick between. The middle one is the default and the one
// the spec named; the other two are the answers a real field gives — a small
// garden wants the round over, a big one wants time to cross it.
export const FUSES = [30_000, 60_000, 90_000];

export const TIMING = {
  arm: 5_000,
  defend: FUSES[1],
  defuse: 10_000,
  hack: 5_000,
  // The takeover: how long a captured terminal celebrates before it can be
  // taken back. Long enough to read as an event, short enough that the fight
  // does not stop.
  takeover: 2_400,
};

// A fresh round, dealt from the host's settings.
//
// The fuse is copied onto the round rather than read from the settings while it
// runs: a charge armed for ninety seconds is armed for ninety seconds, and a
// host changing the setting while it counts must not move a clock the room is
// already watching.
//
// A terminal that starts owned is *immediately* contestable — `since` is backed
// off by a takeover, or the point would spend its first two and a half seconds
// refusing the team that came to take it.
export const start = (mode, now = Date.now(), prefs = {}) => {
  const owner = mode === "terminal" && (prefs.start === "red" || prefs.start === "blue");
  return {
    mode,
    status: owner ? prefs.start : "neutral",
    since: owner ? now - TIMING.takeover : now,
    fuse: FUSES.includes(prefs.fuse) ? prefs.fuse : TIMING.defend,
    hold: null,
  };
};
// How long this round's charge runs. A round saved before fuses existed has no
// number on it, and the default is what it was armed under.
const fuseOf = (state) => (FUSES.includes(state.fuse) ? state.fuse : TIMING.defend);

// What a hold from here would be going for, or null if this state takes no
// hold. One table, both modes, and it is the whole of what "the button knows
// whose side it is on" means: the terminal's button always belongs to whoever
// does not own the point, so its target is read off the current owner.
//
// `side` only matters at neutral, where there is no owner to be the opposite
// of and the room has to say which team took it.
export function target(state, side) {
  if (state.mode === "bomb")
    return state.status === "neutral"
      ? { to: "armed", for: TIMING.arm }
      : state.status === "armed"
        ? { to: "defused", for: TIMING.defuse }
        : // An ended round takes no hold. Clearing it is a tap: the result has
          // been read by the time anybody walks over to the device, and making
          // somebody stand there holding a button to get on with the next round
          // is the app being precious about a decision that is already made.
          null;
  if (state.status === "neutral")
    return side === "red" || side === "blue" ? { to: side, for: TIMING.hack } : null;
  return { to: state.status === "red" ? "blue" : "red", for: TIMING.hack };
}

// Held long enough to matter. Below this a press is a fumble, not an attempt,
// and treating it as one makes the device feel like it is arguing with you.
const NUDGE = 90;

// The reducer. Pure, and the only place a Beacon's state changes.
//
//   hold     a thumb went down on the control (optionally naming a side)
//   release  it came off
//   tick     time passed — this is what completes a hold and what detonates
//   reset    the host cleared an ended round
export function reduce(state, action, now = Date.now()) {
  if (action.type === "reset") return start(state.mode, now, action.prefs);
  if (action.type === "release") return state.hold ? { ...state, hold: null } : state;
  if (action.type === "hold") {
    // A terminal that has just flipped is still playing its takeover. The
    // control is dead for those two seconds and says so, rather than accepting
    // a hold that would be counted against a state the room has not read yet.
    if (state.hold || !settled(state, now)) return state;
    const going = target(state, action.side);
    return going ? { ...state, hold: { ...going, from: now } } : state;
  }
  if (action.type !== "tick") return state;
  if (state.hold && now - state.hold.from >= state.hold.for)
    return { ...state, status: state.hold.to, since: now, hold: null };
  // The clock only runs on an armed charge, and it runs off the timestamp the
  // arming wrote — never off a decrementing tick, so a backgrounded phone that
  // stopped getting frames comes back to the right number rather than a slow
  // one. Same rule as the Event clock in the app.
  if (state.mode === "bomb" && state.status === "armed" && now - state.since >= fuseOf(state))
    return { ...state, status: "detonated", since: now, hold: null };
  return state;
}

// An ended round holds its result until somebody clears it: the outcome is the
// thing the round was for, and a device that has tidied itself away by the time
// anyone walks over to it has thrown that away.
export const ended = (state) => state.status === "detonated" || state.status === "defused";
export const owned = (state) => state.status === "red" || state.status === "blue";
// Whether the control will take a hold. The charge always will — an ended round
// still has its reset — and the terminal will not for the length of a takeover,
// which is the only moment on this device when a thumb is refused.
export const settled = (state, now = Date.now()) =>
  state.mode === "bomb" ? true : !owned(state) || now - state.since >= TIMING.takeover;

export const holdProgress = (state, now = Date.now()) =>
  state.hold ? Math.min(1, Math.max(0, (now - state.hold.from) / state.hold.for)) : 0;
// Milliseconds left on an armed charge. Zero for everything else, so a caller
// never has to ask what mode it is in first.
export const remaining = (state, now = Date.now()) =>
  state.mode === "bomb" && state.status === "armed"
    ? Math.max(0, fuseOf(state) - (now - state.since))
    : 0;
// What the standby readout shows: the round this device is about to run.
export const fuse = fuseOf;
// A press shorter than a nudge is not an attempt. Exported because it is the
// one rule the test can get wrong by guessing.
export const attempted = (state, now = Date.now()) =>
  Boolean(state.hold) && now - state.hold.from >= NUDGE;

/* ---------- persistence ---------- */

// The prop's own state, on the device it is standing on. A charge armed at
// 14:02 is armed at 14:02 whatever happens to the browser in between, so a
// phone that locked, slept, or was dropped comes back to the right clock rather
// than to a fresh device.
//
// No versioning and no migration ladder: this is four fields, and the shape of
// an unreadable one is "start again", which is a legitimate answer for a prop
// that has just been picked up. Same posture as the app's prefs, not its Event.
const STORE = "lets1up-beacon-v1";
// The host's settings, kept apart from the round. They outlive it — the fuse a
// host picked is the fuse they want next time too — and they have no version
// and no migration: an unreadable one is "the defaults", which is a legitimate
// answer for a prop somebody has just picked up. Same posture as the app's prefs.
const PREFS = "lets1up-beacon-prefs-v1";
export function savePrefs(prefs, storage = globalThis.localStorage) {
  try {
    storage?.setItem(PREFS, JSON.stringify(prefs));
  } catch {
    // A full or blocked store costs the setting being remembered and nothing
    // else. It must never be the reason the device stops responding to a thumb.
  }
}
export function loadPrefs(storage = globalThis.localStorage) {
  try {
    return JSON.parse(storage?.getItem(PREFS) || "{}") ?? {};
  } catch {
    return {};
  }
}
export function save(state, storage = globalThis.localStorage) {
  try {
    storage?.setItem(STORE, JSON.stringify({ ...state, hold: null }));
  } catch {
    // A full or blocked store costs the round's recovery and nothing else. It
    // must never be the reason the device stops responding to a thumb.
  }
}
export function load(storage = globalThis.localStorage) {
  try {
    const saved = JSON.parse(storage?.getItem(STORE) || "null");
    return saved?.mode && typeof saved.since === "number" ? { ...saved, hold: null } : null;
  } catch {
    return null;
  }
}

/* ---------- everything below here needs a document ---------- */

const page = globalThis.document;

// The clips, decoded once into memory and fired as throwaway source nodes.
//
// These were `<audio>` elements, and that was the second cause of the mobile
// lag. Every beep did the two most expensive things you can do to one: set
// `currentTime = 0`, which is a *seek* and on iOS can re-initialise the decode
// pipeline, and set `playbackRate` to a random value, which can engage
// pitch-correction time-stretching. Eleven times a second, on elements wired
// into the system audio session.
//
// Web Audio has none of that. The file is decoded to PCM once at boot, a source
// node is a few bytes to create and throw away, rate is a plain resample with no
// time-stretching, and overlapping sounds cost nothing.
let ctx = null;
const sounds = new Map();
let muted = true;
function noise(name, rate = 1) {
  const clip = sounds.get(name);
  if (!clip || muted || !ctx) return;
  const source = ctx.createBufferSource();
  source.buffer = clip;
  source.playbackRate.value = rate;
  source.connect(ctx.destination);
  source.start();
}

// The bed under a hold: one looping source, started when a thumb goes down and
// stopped when it comes up, pitched with progress instead of retriggered. A
// loop is a single node that the audio thread owns for the length of the hold —
// where a retriggered one-shot is a new node, a new schedule and a new mix
// change on every beep, which is what a phone was choking on.
let working = null;
function work(rate) {
  if (!ctx || muted) return;
  const clip = sounds.get("work");
  if (!clip) return;
  if (!working) {
    working = ctx.createBufferSource();
    working.buffer = clip;
    working.loop = true;
    working.connect(ctx.destination);
    working.start();
  }
  working.playbackRate.value = rate;
}
function rest() {
  try {
    working?.stop();
  } catch {
    // Already stopped. A source node cannot be restarted, so this is the end of
    // its life either way.
  }
  working = null;
}

// Decoding happens after the gate, off the copies it already cached — the blob
// URL is local, so this reads memory rather than downloading anything twice.
async function decode(urls) {
  const Sound = globalThis.AudioContext ?? globalThis.webkitAudioContext;
  if (!Sound) return;
  ctx = new Sound();
  await Promise.all(
    Object.entries(urls).map(async ([name, url]) => {
      try {
        const bytes = await fetch(src(url)).then((r) => r.arrayBuffer());
        sounds.set(name, await ctx.decodeAudioData(bytes));
      } catch {
        // A clip this device will not decode is a clip it does not play. It is
        // not a reason for the prop to stop working.
      }
    }),
  );
}
// Audio on a phone needs a gesture, and the first gesture this device gets is
// the thumb that starts the round. An AudioContext built outside one starts
// suspended; resuming it here is the whole unlock.
function unmute() {
  if (!muted) return;
  muted = false;
  ctx?.resume?.();
}

// A prop whose screen goes to sleep in the middle of a round is broken, so the
// lock is taken alongside fullscreen on the first press and re-taken whenever
// the page comes back — a phone that was pocketed drops it silently.
let lock = null;
const hold = async () => {
  // One sentinel at a time. Requesting a second while the first is live drops
  // the reference without releasing it, and the browser keeps it until the page
  // hides — a leaked sentinel per press.
  if (lock && !lock.released) return;
  try {
    lock = (await navigator.wakeLock?.request("screen")) ?? null;
  } catch {
    // Unsupported, or refused because the page is hidden. The device still
    // works; it just dims like any other page.
  }
};
// The wake lock is taken quietly on the first press — it cannot be asked for
// without a gesture, and a prop whose screen sleeps mid-round is broken.
//
// Fullscreen is **not** taken here, and used to be. Entering it resizes the
// viewport and plays the browser's own transition, so the first press on the
// chooser flashed the whole screen on the way into a mode — a flash at the
// exact moment somebody is looking to see whether the thing worked. There is a
// button in the corner for it now, which makes it the host's decision rather
// than something the app does to them on the way past.
const immerse = () => hold();
const corners = {
  fullscreen: () =>
    page.fullscreenElement
      ? page.exitFullscreen?.().catch(() => {})
      : page.documentElement.requestFullscreen?.().catch(() => {}),
  settings: () => {
    const open = panel.hidden;
    if (open) drawPanel();
    panel.hidden = !open;
    gearButton?.setAttribute("aria-expanded", String(open));
  },
  // Back to the chooser without touching the round. The state stays on disk, so
  // coming back to the same mode picks up the charge that is still counting;
  // picking the other one is what starts something new. Leaving is not a
  // decision worth confirming when it throws nothing away.
  back: () =>
    void swap(() => {
      picked = false;
      mount("");
    }),
};
const fullscreenButton = page?.querySelector("#fullscreen"),
  backButton = page?.querySelector("#back"),
  gearButton = page?.querySelector("#gear"),
  panel = page?.querySelector("#panel");
// The host's settings, read once at boot and written on every change.
let prefs = {};

// The sheet. Two rows, because there are two things a host actually wants to
// change and neither belongs on a device somebody is playing with — the fuse a
// field needs, and which side the point opens on.
//
// Rendered from `prefs` rather than toggling classes, so what is on screen
// cannot disagree with what is set.
const SETTINGS = [
  {
    key: "fuse",
    mode: "bomb",
    label: "Fuse",
    note: "How long the site has to be held.",
    options: FUSES.map((ms) => ({ value: ms, text: `${ms / 1000}s` })),
    now: () => fuse(state),
  },
  {
    key: "start",
    mode: "terminal",
    label: "Terminal default",
    note: "Who holds the point at the start of a round.",
    // Red, open, blue — the two sides either side of the middle, which is the
    // order the thing itself is in rather than the order the values happen to
    // be declared in.
    options: [
      { value: "red", text: "RED" },
      { value: "neutral", text: "OPEN" },
      { value: "blue", text: "BLUE" },
    ],
    now: () => state.status,
  },
];

// Only what the device in front of you can act on. A fuse means nothing to a
// control point and a starting side means nothing to a charge, and a sheet that
// lists both makes a host read past half of it to find the half that is theirs.
const settingsFor = (mode) => SETTINGS.filter((row) => row.mode === mode);

// Built once, when the sheet opens. Rebuilding it on every change replaced the
// element and replayed its slide-up, so the sheet bounced under the thumb each
// time a setting was picked — the animation belongs to the sheet arriving, not
// to the settings changing.
function drawPanel() {
  if (!panel) return;
  panel.innerHTML = `<div class="sheet">${settingsFor(state.mode).map(
    (row) => `
      <div class="row">
        <p class="row-label">${row.label}<span>${row.note}</span></p>
        <div class="opts">${row.options
          .map(
            (option) =>
              `<button type="button" class="opt" data-set="${row.key}" data-value="${option.value}"
                 aria-pressed="${String(row.now() === option.value)}">${option.text}</button>`,
          )
          .join("")}</div>
      </div>`,
  ).join("")}<button type="button" class="opt done" data-act="settings">Done</button></div>`;
}

// What actually changes when a setting is picked: which option is pressed.
// Attributes only, so nothing is replaced and nothing re-animates.
function syncPanel() {
  if (!panel || panel.hidden) return;
  for (const row of settingsFor(state.mode))
    for (const option of panel.querySelectorAll(`[data-set="${row.key}"]`))
      option.setAttribute(
        "aria-pressed",
        String(row.now() === (row.key === "fuse" ? Number(option.dataset.value) : option.dataset.value)),
      );
}

// A setting changes the next round, and the round in front of you when that is
// safe. A host in the settings is setting the device up, so a fuse picked while
// the charge is standing by should be the fuse on the readout — and a side
// picked on an idle terminal should be who holds it now. Neither reaches into a
// hold or a running clock: that would move something the room is watching.
function choose(key, raw) {
  const value = key === "fuse" ? Number(raw) : raw;
  prefs = { ...prefs, [key]: value };
  savePrefs(prefs);
  const idle = !state.hold;
  if (key === "fuse" && idle && state.mode === "bomb" && state.status !== "armed")
    state = { ...state, fuse: value };
  if (key === "start" && idle && state.mode === "terminal")
    state = reduce(state, { type: "reset", prefs }, Date.now());
  if (state.mode === "terminal") seedDump();
  save(state);
  mount(state.mode);
  syncPanel();
}
// Whether this browser has the Fullscreen API at all. iOS Safari does not — it
// exposes it for <video> and nothing else — so on an iPhone the corner button
// would be a control that does nothing, which is worse than no button.
const canFullscreen = Boolean(page?.documentElement.requestFullscreen);
// Already installed, so there is no browser chrome left to hide.
const standalone =
  page?.defaultView?.matchMedia?.("(display-mode: fullscreen), (display-mode: standalone)")
    .matches || page?.defaultView?.navigator.standalone === true;
if (page)
  page.addEventListener("fullscreenchange", () => {
    const on = Boolean(page.fullscreenElement);
    fullscreenButton?.setAttribute("aria-pressed", String(on));
    fullscreenButton?.setAttribute("aria-label", on ? "Exit fullscreen" : "Enter fullscreen");
  });

/* ---------- the two faces ---------- */

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];
// What the charge says it is doing while a thumb is on it. Walked by progress
// rather than by a timer, so the last line always lands exactly as the hold
// completes and a five-second arm and a ten-second defuse both read the whole
// sequence — a clock-driven cycle would cut one off mid-phrase and loop the
// other.
//
// Plausible on the first read and funny on the second, which is the register the
// terminal's log already works in. The defuse list is the one that has to sell
// the pressure: it is somebody guessing, and the last two say so.
const SAYS = {
  armed: [
    "SAFETY INTERLOCK",
    "DETONATOR SEEDED",
    "FIRING TRAIN",
    "CHARGE VECTOR",
    "SEQUENCE COMMIT",
  ],
  defused: [
    "COMBINATION READ",
    "ATTEMPT REJECTED",
    "DETONATOR ISOLATE",
    "FIRING LINE SHUNT",
    "WRONG WIRE",
    "OTHER WIRE",
  ],
  // Clearing a round. It is a tap rather than a hold, so this is the one
  // sequence the device performs on its own — and it is the beat between one
  // round and the next, which is exactly when a prop should look busy.
  neutral: ["PURGING SEQUENCE", "DETONATOR SAFE", "SITE CLEAR"],
};
// How long the device takes to clear itself. Presentation only: the round is
// already neutral by the time this runs, and nothing in the reducer knows about
// it. A tap that flips a screen in one frame is the difference between a prop
// and a web page — this is a machine doing something, with the noise to match.
const WIPE = 1_150;

// What each landing sounds like. Every landing has a take of its own now, so the
// fallback below it is dead in practice — it stays because a state added without
// a recording should be a generic confirm rather than silence.
const LANDED = {
  armed: "armed",
  defused: "defused",
  detonated: "detonated",
  red: "secure-red",
  blue: "secure-blue",
};
// One line at a time, on the readout's own bottom row. A running checklist was
// tried and needs vertical room the readout does not have — it is three lines
// tall by design, and growing it to fit a log meant growing the readout, which
// is the device changing size in your hand.
const saying = (to, progress) => {
  const lines = SAYS[to] ?? [];
  return lines[Math.min(lines.length - 1, Math.floor(progress * lines.length))] ?? "";
};
// The machine's own nameplate, printed in the only alphabet the tube has. The
// boot screen and the chooser wear the real drawing; here an SVG would be the
// one object on screen not made of characters.
//
// It says who holds the point: LETS1UP while it is open, then the side that took
// it. Five rows of three-column letters, and the banner is sized from its own
// column count so RED and LETS1UP occupy the same width — a fixed font size
// would render a three-letter word at a third the size of a seven-letter one,
// and the masthead would appear to shrink every time somebody captured it.
const BLOCK = {
  L: ["█  ", "█  ", "█  ", "█  ", "███"],
  E: ["███", "█  ", "███", "█  ", "███"],
  T: ["███", " █ ", " █ ", " █ ", " █ "],
  S: ["███", "█  ", "███", "  █", "███"],
  1: [" █ ", "██ ", " █ ", " █ ", "███"],
  U: ["█ █", "█ █", "█ █", "█ █", "███"],
  P: ["███", "█ █", "███", "█  ", "█  "],
  R: ["██ ", "█ █", "██ ", "█ █", "█ █"],
  D: ["██ ", "█ █", "█ █", "█ █", "██ "],
  B: ["██ ", "█ █", "██ ", "█ █", "██ "],
};
// Every word is padded to the widest, so the nameplate is one fixed grid at one
// fixed size. That is what makes the takeover possible: two words on the same
// grid can be wiped between column by column, where two words of their own
// widths could only be swapped.
const WIDE = 27;
const banner = (word) => {
  const rows = Array.from({ length: 5 }, (_, row) =>
    [...word].map((c) => BLOCK[c][row]).join(" "),
  );
  const pad = WIDE - rows[0].length,
    left = Math.floor(pad / 2);
  return rows.map((row) => " ".repeat(left) + row + " ".repeat(pad - left)).join("\n");
};
const MARKS = { neutral: banner("LETS1UP"), red: banner("RED"), blue: banner("BLUE") };

// The takeover, in the nameplate. The incoming side's word comes in from the
// left as the hack lands, the outgoing one is still standing to the right of it,
// and a column of noise sits on the seam between them — the point being fought
// over, written in the one place on the tube big enough to read across a garden.
//
// Two nodes per row rather than one string, because the taken half wears the
// incoming colour: the shape says a word is being replaced and the colour says
// whose it is becoming. Every slice is sized so the row stays exactly `WIDE`
// characters — the five rows are a grid, and one short row shears the letters.
function paintMark(now) {
  const node = roles.mark;
  if (!node) return;
  const held = MARKS[state.status] ?? MARKS.neutral;
  if (!state.hold) {
    if (node.textContent !== held) node.textContent = held;
    return;
  }
  const to = (MARKS[state.hold.to] ?? held).split("\n"),
    edge = Math.floor(holdProgress(state, now) * WIDE);
  const html = held
    .split("\n")
    .map(
      (row, r) =>
        `<b>${to[r].slice(0, Math.max(0, edge - 1))}</b>${edge > 0 ? "▓" : ""}${row.slice(edge)}`,
    )
    .join("\n");
  if (node.innerHTML !== html) node.innerHTML = html;
}
const GLYPHS = "!@#$%^&*()_+-=[]{}|;:,.<>?/\\~0123456789ABCDEF";
const glyph = () => GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
// The cells an arming charge has not taken yet. Shifting fill rather than random
// symbols: this line is one bar made of characters, and a cell that flickers
// between densities reads as charge moving through it where a cell that flickers
// between `@` and `%` reads as a fault.
// `Math.floor`, not `| 0`: a timestamp over ten billion overflows a 32-bit
// bitwise op and comes back negative, which indexes past the front of this array
// and joins `undefined` in as nothing. Three cells of the bar silently vanished.
const SHADE = ["░", "▒", "▓", "▒"];
const clock = (ms) => {
  const whole = Math.ceil(ms / 1000);
  return `${String(Math.floor(whole / 60)).padStart(2, "0")}:${String(whole % 60).padStart(2, "0")}`;
};

// The chooser, the charge, and the terminal. Built once when a mode is entered
// rather than re-rendered per frame: the paint below only writes text and
// attributes, so nothing in the device is ever replaced under a thumb that is
// resting on it.
const shells = {
  // Centred, and it wears the mark: this is the one screen that is a page rather
  // than a device, so it is the one place the publisher signs its name. Left
  // aligned it read as a column shoved against the edge of the phone with the
  // rest of the screen unaccounted for.
  choose: () => `
    <div class="choose">
      <p class="maker" role="img" aria-label="Lets1UP"><svg viewBox="0 0 365 132" aria-hidden="true"><use href="#mark"/></svg></p>
      <h1>BEACON</h1>
      <p class="lede">Pick the objective.<br>Put the screen where the game is.</p>
      ${
        canFullscreen || standalone
          ? ""
          : // The one place the install is worth mentioning, and only on a device
            // that cannot do it the other way. Said once, on the way in, rather
            // than as a banner over a running round.
            `<p class="lede install">Add to Home Screen for fullscreen.</p>`
      }
      <div class="picks">
        <button type="button" class="pick" data-mode="bomb"><b>BOMB</b><span>Arm it. Hold the site for a minute.</span></button>
        <button type="button" class="pick" data-mode="terminal"><b>TERMINAL</b><span>Take the point. Take it back.</span></button>
      </div>
    </div>`,
  // The casing is the Pack's render — moulded plastic, straps, its own wire loom.
  // Everything laid on it is a module bolted to the case: the readout, the pad
  // and the maker's plate. All of it is scenery; what a thumb can reach is the
  // one bar at the bottom.
  //
  // The four SVG wires that used to arc over the top went with the render. They
  // were a drawing of wires on a drawing of a case, and the case now has real
  // ones — two drawings of the same thing is what makes a composite look like
  // a composite.
  bomb: () => `
    <div class="c4">
      <div class="stage" aria-hidden="true"></div>
      <div class="rack">
      <div class="lcd">
        <div class="lcd-glass">
          <p class="lcd-line" data-role="top">C4 // STANDBY</p>
          <p class="lcd-big" data-role="big">--:--</p>
          <p class="lcd-line" data-role="bottom">AWAITING SEQUENCE</p>
          <i class="led" data-role="led" aria-hidden="true"></i>
        </div>
      </div>
      <!-- The pad keeps the panel's centre line to itself and the mark takes the
           dead plate to its right — the one piece of the panel nothing is bolted
           to. Laid over that space rather than beside the pad in a row, because
           a row pushes the keys off the axis everything else on this device is
           centred on. Above the readout it fouled the panel's raised lip. -->
      <div class="pad-row">
        <div class="keypad" aria-hidden="true">
          ${KEYS.map((k) => `<i data-key="${k}">${k}</i>`).join("")}
        </div>
        <p class="plate" role="img" aria-label="Lets1UP"><svg viewBox="0 0 365 132" aria-hidden="true"><use href="#mark"/></svg></p>
      </div>
      <!-- The control belongs on the case, in the panel, with the readout and
           the pad. Outside it, it floated below the lower strap on a part of the
           casing nothing else was bolted to. -->
      <div class="deck-slot"></div>
      </div>
    </div>`,
  // No bezel and no frame: the screen *is* the device. A drawn tube around it
  // was a picture of a terminal, and a phone held up in a garden is better at
  // being one than any border can make it look.
  //
  // The dump is two nodes rather than one, and that is the whole of the
  // takeover: `.taken` holds the rows the challenger has rewritten and wears
  // their colour, `.rest` holds what is still the owner's. Colouring rows
  // individually would be thirty-four elements rewritten per frame; this is two
  // strings and one custom property.
  terminal: () => `
    <div class="crt">
      <div class="screen">
        <pre class="mark" data-role="mark" aria-label="Lets1UP"></pre>
        <p class="head">LETS1UP INDUSTRIES (TM) TERMLINK PROTOCOL</p>
        <p class="head" data-role="top">CONTROL POINT — HOLD TO CLAIM</p>
        <p class="attempts" data-role="attempts">ATTEMPT(S) LEFT: <b>■ ■ ■ ■</b></p>
        <div class="dump"><b class="taken"></b><span class="rest"></span></div>
        <p class="prompt" data-role="bottom">&gt;<span class="caret"></span></p>
      </div>
    </div>`,
};

// The control. Two buttons only at a neutral terminal, where there is no owner
// for the button to be the opposite of and the room has to say who took it.
const controls = (state) =>
  state.mode === "terminal" && state.status === "neutral"
    ? `<button type="button" class="grip red" data-side="red"><span class="grip-label"></span></button>
       <button type="button" class="grip blue" data-side="blue"><span class="grip-label"></span></button>`
    : `<button type="button" class="grip ${sideOf(state)}" data-side=""><span class="grip-label"></span></button>`;

// What colour the one control wears: the side whose action it is.
//
// On the terminal that is the **challenger**, never the owner — it read as the
// red team's own button on a red terminal, which is the one thing this control
// must never say. On the charge the two jobs are themselves a pair of sides:
// **arming is red and defusing is blue**, the way the game they come from splits
// them, so the button names the team being asked before anybody reads the word
// on it. Clearing a result belongs to nobody and keeps the device's own colour.
const SIDE_OF = { armed: "red", defused: "blue" };
const sideOf = (state) => {
  const going = target(state)?.to ?? "";
  return state.mode === "terminal" ? going : (SIDE_OF[going] ?? "");
};

// What the one control says: the verb, and nothing else.
//
// It said HOLD TO DEFUSE and the like, which is a sentence on a button — and
// the button is not where that sentence belongs. There is a display right above
// it whose whole job is saying what the device wants, the fill teaches the hold
// inside half a second of the first press, and a one-word control is legible
// across a garden where four words are a grey smear. So the screen carries the
// instruction and the button carries the verb.
// Under a thumb it says **HOLD**, and only on the button being held. The verb
// names what pressing does; the press names what keeping it down does. That is
// the whole instruction in two words which are never on screen at once, and it
// answers the thing a player cannot otherwise tell in the first half-second —
// that letting go now is letting go of something.
//
// `side` is the button's own, which only a neutral terminal has: that is the one
// state with two of them, and each says its own team until a thumb is on it.
const gripLabel = (state, side = "", holding = false) => {
  if (state.mode === "bomb")
    return holding
      ? "HOLD"
      : state.status === "neutral"
        ? "ARM"
        : state.status === "armed"
          ? "DEFUSE"
          : "RESET";
  // Bracketed, because on the terminal the control is a line of the machine's
  // own output rather than a button laid over it.
  if (holding) return "[ HOLD ]";
  return `[ ${(side || (state.status === "red" ? "blue" : "red")).toUpperCase()} ]`;
};

/* ---------- the door ---------- */

// The iris, and it is the TV's: the same circle, the same centre, the same
// numbers. Shutting is a touch quicker than opening, because you are done with
// the place you are leaving and the arrival is the part worth watching. On the
// TV it is the door between a Game and the deck; here it is the door between one
// objective and another, which is the same move for the same reason — a mode is
// a place, and a place should be arrived at rather than swapped in.
//
// Driven by the Web Animations API rather than GSAP, which the prop does not
// load. `power2.out` is a quadratic ease-out, which is this curve.
const IRIS = { at: "50% 42%", ms: 700, ease: "cubic-bezier(.25,.46,.45,.94)" };
const reducedMotion = () => page.defaultView?.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
// At most one of these is ever alive. `fill: forwards` is what holds the screen
// shut across the markup swap, and it is also what keeps the Animation object
// attached to the element after it finishes — so the shut half has to be
// cancelled explicitly, or every mode change leaves one behind for good.
// `getAnimations()` grew without bound, and each survivor is style work on every
// frame after it.
let door = null;
function iris(open) {
  if (!root?.animate || reducedMotion()) return Promise.resolve();
  door?.cancel();
  const shut = `circle(0% at ${IRIS.at})`,
    wide = `circle(80% at ${IRIS.at})`;
  door = root.animate([{ clipPath: open ? shut : wide }, { clipPath: open ? wide : shut }], {
    duration: open ? IRIS.ms : IRIS.ms * 0.8,
    easing: IRIS.ease,
    fill: "forwards",
  });
  // Cancelling rejects `finished`, and a cancelled door is one somebody opened
  // again — not an error.
  return door.finished.catch(() => {});
}

// The blast. White in fast and out slow, which is the shape of the thing it is
// standing in for — a detonation is instant and what follows is the eye
// recovering. Symmetrical timing reads as a fade rather than a bang.
//
// It is deliberately *over* the device rather than a state of it: the charge has
// already flipped to its detonated screen underneath, so the white lifts to
// reveal the result rather than cutting to it. That is the "back to origin"
// half, and it costs nothing — there is nothing to restore, because nothing was
// changed.
//
// Tracked and cancelled like the door above, for the same reason: two rounds
// detonated in quick succession would otherwise leave the first animation
// attached to the element for good.
const BLAST = { up: 90, down: 1_100 };
let flare = null;
function blast() {
  const sheet = page.getElementById("blast");
  if (!sheet?.animate || reducedMotion()) return;
  flare?.cancel();
  flare = sheet.animate(
    [{ opacity: 0 }, { opacity: 1, offset: BLAST.up / (BLAST.up + BLAST.down) }, { opacity: 0 }],
    // No `fill`, so the element returns to its stylesheet `opacity: 0` on its
    // own and the finished animation has nothing to hold it at.
    { duration: BLAST.up + BLAST.down, easing: "cubic-bezier(.2,0,.4,1)" },
  );
  flare.finished.catch(() => {}).then(() => (flare = null));
}

// One place to another: shut, swap, open. The corners fade with it rather than
// popping — they are chrome over the door, and a hard cut on them during a
// smooth wipe is the thing that reads as a flash.
let swapping = false;
async function swap(change) {
  if (swapping) return;
  rest();
  // The purge belongs to the screen being left, so it goes with it. Two quick
  // resets used to stack two timers, and leaving a mode mid-purge left one to
  // fire against markup that was already gone.
  clearTimeout(purge);
  purge = 0;
  wiping = 0;
  page.body.dataset.wiping = "no";
  if (panel && !panel.hidden) corners.settings();
  swapping = true;
  page.body.dataset.swapping = "yes";
  try {
    await iris(false);
    change();
    await iris(true);
  } finally {
    // In a `finally`, because a throw inside `change()` used to leave this true
    // for the life of the page — and every later mode change would return at the
    // guard above without doing anything. A dead back button, silently.
    door?.cancel();
    door = null;
    page.body.dataset.swapping = "no";
    swapping = false;
  }
}

/* ---------- the paint ---------- */

// When the charge's self-clear finishes, and the timer that ends it.
// Presentation only — see WIPE.
let wiping = 0,
  purge = 0;
let state = null,
  // Whether a mode has actually been picked. `state` always holds one so the
  // reducer never has to answer questions about a device that is not a device
  // yet, which means it cannot also be the thing that says a mode is on screen
  // — without this the chooser wore the charge's skin.
  picked = false,
  root = null,
  grips = [],
  shown = "",
  // The dump's rows are rebuilt only while something is happening to them, so a
  // terminal nobody is touching costs one class change a second rather than
  // sixteen rows of string building.
  dumpRows = [],
  lastTick = 0,
  lastKey = 0;

// The readout's own nodes, looked up once when the shell is built. They were
// being found by attribute selector on every write, five times a frame — cheap
// on a desk and not free on a phone.
let roles = {},
  dumpHead = null,
  dumpTail = null;
const readRoles = () => {
  roles = {};
  for (const node of root?.querySelectorAll("[data-role]") ?? []) roles[node.dataset.role] = node;
  dumpHead = root?.querySelector(".dump .taken") ?? null;
  dumpTail = root?.querySelector(".dump .rest") ?? null;
};
const write = (role, text) => {
  const node = roles[role];
  if (node && node.textContent !== text) node.textContent = text;
};

// Keyed on the shell *and* the control it carries, because those are the two
// things that decide what elements exist. A neutral terminal offers two buttons
// and an owned one offers a single challenger's button, and keying on the mode
// alone left both team buttons standing after the point had been taken.
// Everything else about a state is text and attributes, and `paint` writes
// those without replacing anything.
function mount(mode) {
  const shell = mode || "choose";
  const key = shell + (mode ? controls(state) : "");
  if (shown === key) return;
  shown = key;
  // The skin changes *before* the markup does. These attributes carry every
  // colour and every layout rule the shell about to be written depends on, and
  // `paint` does not run until the next frame — so setting them there left one
  // frame of the terminal drawn in the charge's tokens on the way in, which is
  // the flash between modes.
  page.body.dataset.mode = mode || "";
  page.body.dataset.status = state?.status ?? "neutral";
  page.body.dataset.going = "";
  page.body.dataset.holding = "no";
  root.innerHTML = shells[shell]();
  if (mode) {
    // A shell that declares a slot wants the control inside it — the charge does,
    // because the panel it is bolted to is part of the picture. One that does not
    // gets it laid over the top, which is the terminal: a soft key on a tube.
    const deck = page.createElement("div");
    deck.className = "deck";
    deck.innerHTML = controls(state);
    (root.querySelector(".deck-slot") ?? root).append(deck);
  }
  grips = [...root.querySelectorAll(".grip")];
  readRoles();
  dumpRows = [];
  if (mode === "terminal") seedDump();
}

// The terminal's own session log, and it is the whole difference between a
// screen of text and a screen that reads as a machine somebody has been using.
// It was thirty-four rows of hex garbage, which is authentic and says nothing —
// a player standing over the point for ninety seconds reads it, and it should
// pay them for that.
//
// One command, one or more lines of output, in the register of a node that has
// been logging a children's party all afternoon. Nothing here is state: the log
// is scenery, and the live status is the two lines above it.
//
// This is public copy, so the trademark rule in ai/docs/vocabulary.md applies to
// every line of it: darts, foam, blasters, and never the brand name.
const LOG = [
  "> auth --node SECTOR-7",
  "  identity ... UNVERIFIED",
  "  posture .... SUSPICIOUS",
  "",
  "> uptime",
  "  node up 4 min. morale nominal.",
  "  snack reserves CRITICAL.",
  "",
  "> ls /objectives",
  "  sector-7   the shed   the big tree",
  "  dad's car  [ RESTRICTED ]",
  "",
  "> cat /etc/house.rules",
  "  1. eye protection, or no protection",
  "  2. the trampoline is not cover",
  "  3. refer to rule 1",
  "",
  "> ping hq",
  "  hq unreachable. hq is in the kitchen.",
  "",
  "> df -h /ammo",
  "  /ammo  92% full   reload advised",
  "",
  "> ps aux | grep fun",
  "  fun   running   pid 1   do not kill",
  "",
  "> tail -f /var/log/incidents",
  "  14:02  perimeter breached by a 9 year old",
  '  14:03  operator calls it "a warm up"',
  "  14:07  foam recovered from gutter",
  "  14:11  someone has the high ground",
  "",
  "> sudo declare victory",
  "  operator is not in the sudoers file.",
  "  this incident will be reported.",
  "",
  "> _",
];

// What a breach writes over the log as it lands. Plausible on the first read and
// funnier on the second, which is the same trick the log plays — a hack that
// scrolled pure noise looked like a crash rather than an intrusion.
// What the machine says you can do, in its own voice and at the top of its own
// log. The header line names the state; this names the move — how long a claim
// takes, that letting go loses it, and that it will be taken back. A prop gets
// handed to somebody who has never seen it, in a garden, mid-game, and the one
// screen it has should answer them without anybody being fetched to explain.
const brief = (state) =>
  state.status === "neutral"
    ? [
        "> help --claim",
        "  hold RED or BLUE to take this point",
        "  a claim runs 5 seconds. do not let go.",
        "  it can always be taken back.",
        "",
      ]
    : [
        "> help --claim",
        `  sector held by ${state.status.toUpperCase()}`,
        `  hold ${state.status === "red" ? "BLUE" : "RED"} to take it back`,
        "  5 seconds, uninterrupted",
        "",
      ];

const BREACH = [
  "> breach --sector 7 --force",
  "  bypassing 0x7F3A ......... ok",
  "  spoofing badge ........... ok",
  "  rewriting sector table ... ok",
  "  bribing the node ......... ok",
  "  node asks for two sweets .",
  "  paying ................... ok",
  "  ACCESS ESCALATED",
  "  overwriting owner record",
  "  previous owner: NOT PLEASED",
  "  flushing their high scores",
  "  laughing() ............... ok",
];
// Rows are the log, then whatever a breach has written over the top of them.
// More than any phone can show, on purpose: the dump is masked at its bottom
// edge, so a surplus reads as a machine with more log than screen and a
// shortfall reads as a page that failed to load.
function seedDump() {
  // The brief first, then the flavour. A breach overwrites from the top, so the
  // help is the first thing the other team's colour eats — which is the right
  // order: by the time somebody is being hacked they have read it.
  dumpRows = [...brief(state), ...LOG];
  paintDump(0);
}

// Split the dump at the row the hack has reached. Everything above it has been
// rewritten by the challenger and is theirs; everything below is still the
// owner's. `taken` is 0 when nothing is being hacked, which is one empty string
// and the whole dump in one colour.
function paintDump(taken) {
  const head = dumpHead,
    tail = dumpTail;
  if (!head || !tail) return;
  const above = dumpRows.slice(0, taken).join("\n");
  head.textContent = above && `${above}\n`;
  tail.textContent = dumpRows.slice(taken).join("\n");
}

// One frame. Every branch here is text, a class or a custom property — no
// element is created, moved or replaced, so a thumb resting on the control
// never has the control taken out from under it.
// How often the readout is redrawn while a thumb is down. The fill under the
// label is the only thing that benefits from a frame-perfect update — it is a
// bar, and a bar that steps is a bar that looks broken. Everything else is a
// *scramble*: characters rolling too fast to read, where sixty a second and
// twelve a second are the same picture and one of them is five times the work.
//
// This is the whole of the mobile problem. Idle the loop ticks five times a
// second; the moment a hold starts it went to sixty, and every one of those
// frames rebuilt strings, wrote text and re-laid-out a grid. A phone held 3-10
// fps through it while the countdown next door sat at 55.
const DRAW_MS = 80;
let drawn = 0;

function paint(now) {
  const progress = holdProgress(state, now),
    live = settled(state, now),
    body = page.body;
  body.dataset.mode = picked ? state.mode : "";
  // Nothing to go back from on the chooser, and nothing worth filling a screen
  // for either. The fullscreen corner additionally goes where the API is not
  // there to answer it, or where the app is already installed and there is no
  // chrome left to take away.
  if (backButton) backButton.hidden = !picked;
  if (gearButton) gearButton.hidden = !picked;
  if (fullscreenButton) fullscreenButton.hidden = !picked || !canFullscreen || standalone;
  body.dataset.status = state.status;
  body.dataset.holding = state.hold ? "yes" : "no";
  // What this hold is going for. One attribute doing two jobs, because they are
  // the same fact: which way the fill travels, and — on the terminal — whose
  // colour is arriving. A hack is one thing happening to the machine, so the
  // rows, the target line and the button all read the same value.
  body.dataset.going = state.hold?.to ?? "";
  // The last ten seconds of an armed charge. The beep closes up here and so
  // does the backlight; it is one fact about the round, so it is one attribute.
  const left = remaining(state, now);
  body.dataset.urgent = left > 0 && left < 10_000 ? "yes" : "no";
  body.dataset.live = live ? "yes" : "no";

  // The cheap half runs every frame; the readout runs at DRAW_MS.
  if (now - drawn >= DRAW_MS || !state.hold) {
    drawn = now;
    if (state.mode === "bomb") paintBomb(now, progress);
    else paintTerminal(now, progress);
  }

  for (const grip of grips) {
    // Whether this is the button the thumb is on. It decides both the fill and
    // the word, which is the same question asked twice: a neutral terminal shows
    // two buttons, and the other team's must not report this team's progress.
    const mine = Boolean(state.hold && (!grip.dataset.side || grip.dataset.side === state.hold.to));
    const label = grip.firstElementChild;
    // While the charge is clearing itself the control keeps the word it had:
    // the state is already neutral, and letting the label run ahead of the
    // sequence is the same jump the deck is being held back from.
    //
    // Compared before writing, and that is not a micro-optimisation: assigning
    // `textContent` destroys and rebuilds the text node even when the string is
    // identical, so this was tearing down and relaying out the label sixty times
    // a second inside a rounded, clipped, shadowed button — for a word that
    // changes twice in a round.
    const word = now < wiping ? "RESET" : gripLabel(state, grip.dataset.side, mine);
    if (label && label.textContent !== word) label.textContent = word;
    // Blocked, never `disabled`. A disabled button swallows pointer events
    // outright, so a press during a takeover would do nothing and say nothing —
    // and a control that answers a thumb with silence is the one a player
    // decides is broken. It greys, it still takes the press, and the reducer is
    // the thing that refuses it.
    const blocked = live ? "false" : "true";
    if (grip.getAttribute("aria-disabled") !== blocked) grip.setAttribute("aria-disabled", blocked);
    // Quantised to half a percent: a custom property write invalidates style for
    // the element and its `::before`, and below this the bar has not moved a
    // device pixel. Three hundred writes over a five-second hold become two
    // hundred, and none of them is wasted.
    const fill = mine ? Math.round(progress * 200) / 200 : 0;
    if (grip.dataset.fill !== String(fill)) {
      grip.dataset.fill = String(fill);
      grip.style.setProperty("--fill", String(fill));
    }
    // The moment a thumb goes down, the screen commits to that side: the other
    // team's button goes and the held one takes the whole bar. Only a neutral
    // terminal ever has two, and once one of them is being held the other is a
    // control nobody in this moment can use.
    //
    // Hidden rather than re-rendered, because re-rendering the deck would
    // destroy the element the thumb is resting on and the hold would die with
    // it. The grid has one column left, so the survivor grows into it.
    grip.hidden = Boolean(state.hold) && !mine;
  }
}

function paintBomb(now, progress) {
  const left = remaining(state, now);
  if (now < wiping) {
    // The purge. Cells empty left to right behind a bright head, which is a
    // wipe running across the readout rather than a value being replaced.
    const done = 1 - (wiping - now) / WIPE,
      head = Math.floor(done * 8);
    write("top", "CLEARING");
    write(
      "big",
      Array.from({ length: 8 }, (_, i) => (i < head ? "░" : i === head ? "█" : "▓")).join(""),
    );
    write("bottom", saying("neutral", done));
    work(1.5 + done * 0.5);
    if (now - lastTick > 70) {
      lastTick = now;
      const keys = root.querySelectorAll("[data-key]");
      for (const key of keys) key.classList.remove("lit");
      keys[Math.floor(Math.random() * keys.length)]?.classList.add("lit");
    }
    return;
  }
  if (state.hold) {
    // The device working. Arming counts its sequence up; defusing throws
    // combinations at the lock and fails at them, which is the same five
    // seconds wearing a much more frightening face.
    const arming = state.hold.to === "armed";
    const step = Math.min(4, Math.floor(progress * 4) + 1);
    {
      write("top", arming ? `ARMING SEQ ${step}/4` : "BYPASS // ATTEMPT");
      // Both faces are characters on the readout's own line, which is what keeps
      // the display one height: a bargraph element was a different height from
      // the clock, so the panel changed shape the moment a thumb went down.
      //
      // Arming solidifies — nine cells of shifting fill that lock to solid from
      // the left as the charge takes. Defusing guesses — four digits, locking
      // one at a time. Same idea, and the difference between them is the
      // difference between a machine working and a machine trying things.
      write(
        "big",
        arming
          ? Array.from({ length: 8 }, (_, i) =>
              i / 8 < progress ? "█" : SHADE[(i + Math.floor(now / 90)) % SHADE.length],
            ).join("")
          : Array.from({ length: 4 }, (_, i) =>
              i / 4 < progress ? String(Math.floor(now / 97 + i) % 10) : glyph(),
            ).join(""),
      );
      // The device narrating itself. The bar carries the number; this is what
      // makes five seconds read as a procedure being worked through rather than
      // a wait being sat out.
      write("bottom", saying(state.hold.to, progress));
    }
    // The bed rises with the fill: the sound is the progress bar, and a thumb
    // learns the rhythm faster than it reads the number.
    work(0.82 + progress * 0.75);
  } else if (state.status === "armed") {
    write("top", "ARMED // DEFEND");
    write("big", clock(left));
    write("bottom", "HOLD THE SITE");
    // The last ten seconds are the ones the room is meant to feel, so the beep
    // closes up as they run out.
    const gap = left < 10_000 ? 250 : left < 30_000 ? 600 : 1_000;
    if (now - lastTick > gap) {
      lastTick = now;
      noise("tick", left < 10_000 ? 1.5 : 1);
    }
  } else if (state.status === "detonated") {
    write("top", "DETONATED");
    write("big", "00:00");
    write("bottom", "SITE LOST · TAP TO RESET");
  } else if (state.status === "defused") {
    write("top", "DISARMED");
    write("big", "SAFE");
    write("bottom", "SITE HELD · TAP TO RESET");
  } else {
    write("top", "C4 // STANDBY");
    // The clock it is about to start, not a row of dashes: a device showing the
    // length of the round it will run answers the question somebody standing
    // over it actually has, and a placeholder answers none.
    write("big", clock(fuse(state)));
    // The instruction, on the display rather than on the button. This is the
    // first line anybody reads on a device they have never seen, and it is the
    // only place the word "hold" has to appear at all.
    write("bottom", "HOLD TO ARM");
  }
  // The keypad lights under nobody's fingers. It is the clearest thing in the
  // whole device about what this file is for: the complexity is a performance.
  //
  // It goes dark the moment the hold does, rather than leaving the last key it
  // happened to land on lit — a single lit key on an idle device reads as a
  // number somebody has typed, which is a fact the room would act on.
  if (!state.hold || now - lastKey > 90) {
    lastKey = now;
    const keys = root.querySelectorAll("[data-key]");
    for (const key of keys) key.classList.remove("lit");
    if (!state.hold) return;
    keys[Math.floor(Math.random() * keys.length)]?.classList.add("lit");
  }
}

function paintTerminal(now, progress) {
  const holding = Boolean(state.hold),
    taking = !settled(state, now);
  // The nameplate says who holds it, and shows it being taken while it is.
  paintMark(now);
  write(
    "top",
    taking
      ? `SECTOR CONTROL: ${state.status.toUpperCase()}`
      : holding
        ? `BREACHING // TARGET ${state.hold.to.toUpperCase()}`
        : state.status === "neutral"
          ? "CONTROL POINT — HOLD TO CLAIM"
          : `SECTOR CONTROL: ${state.status.toUpperCase()}`,
  );
  const bars = Math.max(0, 4 - Math.floor(progress * 4));
  write(
    "attempts",
    holding ? `ATTEMPT(S) LEFT: ${"■ ".repeat(bars).trim() || "—"}` : "ATTEMPT(S) LEFT: ■ ■ ■ ■",
  );
  // The prompt is the line you are typing into while you hold, so it is the one
  // that has to look alive: a longer run of characters, re-rolled every frame,
  // and white for as long as a thumb is down. It is the only white on a
  // monochrome tube, which is what makes it read as the thing being done rather
  // than more of the machine's output.
  write("bottom", holding ? `>${Array.from({ length: 12 }, glyph).join("")}` : ">");
  if (holding) work(0.82 + progress * 0.75);
  if (holding && now - lastTick > 70) {
    lastTick = now;
    // The breach writes over the log from the top down, and the rows it has
    // reached go over to the challenger's colour. What the room sees is their
    // own colour eating the machine a line at a time — one array write, one
    // split, and no state at all.
    //
    // Every fourth line is glyph noise rather than a sentence, so it reads as a
    // machine under attack instead of a machine being politely reconfigured.
    const row = Math.min(dumpRows.length - 1, Math.floor(progress * dumpRows.length));
    dumpRows[row] =
      row % 4 === 3
        ? `  ${Array.from({ length: 26 }, glyph).join("")}`
        : BREACH[row % BREACH.length];
    paintDump(row + 1);
  }
}

/* ---------- the loop ---------- */

function step() {
  const now = Date.now();
  const before = state;
  state = reduce(state, { type: "tick" }, now);
  if (state !== before) {
    // A transition is the one moment worth a sound of its own and worth writing
    // to disk. Nothing else here persists — a hold in progress is not a fact
    // about the round, it is a thumb.
    save(state);
    // The hold that just landed is over, so its bed stops with it — before the
    // one-shot that marks the landing, or the two overlap.
    rest();
    // A transition can change which controls exist — neutral's two buttons
    // become the challenger's one — so the deck is rebuilt here rather than
    // only where a thumb happens to land.
    mount(state.mode);
    if (state.mode === "terminal" && owned(state)) seedDump();
    // The moments with a take of their own get it; everything else gets the
    // generic confirm. A state with no entry here is not a missing sound, it is
    // a moment nobody has recorded a line for yet.
    noise(LANDED[state.status] ?? (ended(state) || owned(state) ? "done" : "rise"));
    // Fired after `mount`, so the white lifts onto the detonated screen rather
    // than onto the armed one it replaced.
    if (state.status === "detonated") blast();
  }
  paint(now);
  // Frames only while something is actually moving. A hold and a takeover are
  // the two things drawn per frame; everything else on this device is a clock
  // that changes once a second and a colour that does not change at all.
  //
  // This is a battery decision, not a tidiness one: a prop is left switched on
  // in a garden for an hour, and sixty repaints a second of a screen showing
  // `00:47` is the difference between a device that lasts the party and one
  // that has to be rescued from a bush to be charged.
  const busy = Boolean(state.hold) || !settled(state, now) || now < wiping;
  if (busy) requestAnimationFrame(step);
  else setTimeout(step, 200);
}

function press(event) {
  const option = event.target.closest?.("[data-set]");
  if (option) {
    unmute();
    return choose(option.dataset.set, option.dataset.value);
  }
  const corner = event.target.closest?.("[data-act]");
  if (corner) {
    unmute();
    return corners[corner.dataset.act]?.();
  }
  // The sheet is a layer over the device: pressing the ground behind it shuts
  // it, and nothing behind it takes the press. One click back, in the place the
  // thumb already is.
  if (panel && !panel.hidden) return corners.settings();
  // The pad is a hold surface too, not just scenery. It is a keypad on a bomb:
  // somebody handed this device is going to press the keys, and a pad that
  // lights up under a thumb while doing nothing is a control that looks broken.
  // Holding anywhere on it is holding the button — same action, same reducer,
  // and the keys were already lighting for exactly this gesture.
  // Everything the device is made of takes the hold, not just the button. This
  // is a prop being grabbed at in a garden by somebody who is running: the pad
  // on the charge, the tube on the terminal, and both readouts. A control that
  // needs to be hit accurately is one that gets missed.
  //
  // The terminal's screen is only useful once the point is owned — at neutral
  // there are two buttons and a press on the tube cannot say which side made it,
  // so the reducer refuses it and the two buttons are the way in.
  const grip = event.target.closest?.(".grip, .keypad, .screen, .lcd");
  const pick = event.target.closest?.(".pick");
  // A press anywhere is the gesture that buys fullscreen, the wake lock and the
  // audio unlock — including a press on a blocked control, which is exactly the
  // moment a player is most likely to be jabbing at the thing.
  if (pick) {
    unmute();
    immerse();
    const mode = pick.dataset.mode;
    return void swap(() => {
      // The mode already on disk is resumed, not restarted. Going out to the
      // chooser and back is how a host checks what the other mode is, and it
      // must not be the thing that disarms a charge somebody is defending.
      if (state?.mode !== mode) state = start(mode, Date.now(), prefs);
      picked = true;
      save(state);
      mount(state.mode);
    });
  }
  if (!grip) return;
  unmute();
  immerse();
  // An ended round clears on the press, not on a hold — it is the one control
  // on this device that is a tap, because it is the one that is not a contested
  // action somebody could be interrupted halfway through.
  if (ended(state)) {
    const purging = state.mode === "bomb";
    state = reduce(state, { type: "reset", prefs });
    save(state);
    if (!purging) {
      mount(state.mode);
      return;
    }
    // The control is deliberately *not* rebuilt here. The round is neutral the
    // moment it is cleared, so remounting would swap the deck to the charge's
    // red ARM key mid-purge — a frame of an empty button, then a colour change,
    // then a width change, all while the display is still clearing itself. The
    // button a thumb has just come off stays exactly as it was and the deck is
    // rebuilt once, at the end, when there is something new to say.
    wiping = Date.now() + WIPE;
    page.body.dataset.wiping = "yes";
    noise("hold", 0.85);
    clearTimeout(purge);
    purge = setTimeout(() => {
      wiping = 0;
      rest();
      page.body.dataset.wiping = "no";
      mount(state.mode);
      noise("done");
    }, WIPE);
    return;
  }
  state = reduce(state, { type: "hold", side: grip.dataset.side }, Date.now());
  if (state.hold) noise("arm");
}

function release() {
  if (!state?.hold) return;
  rest();
  const partial = attempted(state);
  state = reduce(state, { type: "release" });
  // Letting go early is a failed attempt, and the device says so rather than
  // going quiet — a control that gives nothing back on release is one a player
  // assumes is broken.
  if (partial) noise("hold", 1.6);
  if (state.mode === "terminal") seedDump();
}

// A frame-rate readout, behind `?fps=1`. Off by default and not referenced from
// anywhere in the running app, so it costs nothing when it is not asked for.
//
// It runs its own `requestAnimationFrame` loop rather than counting the device's
// own, because that one deliberately drops to a 200ms timer when nothing is
// moving — a true frame rate is what is wanted here, and the number to watch is
// the one during a hold.
//
// `worst` is the useful half. A mean of 60 with a 90ms frame in it is a device
// that stutters once a second, and a mean alone hides that.
function meter() {
  const node = page.createElement("div");
  node.id = "fps";
  node.textContent = "…";
  page.body.append(node);
  let frames = 0,
    worst = 0,
    since = performance.now(),
    last = since;
  const tick = (now) => {
    const gap = now - last;
    last = now;
    frames++;
    if (gap > worst) worst = gap;
    if (now - since >= 1000) {
      const fps = Math.round((frames * 1000) / (now - since));
      node.textContent = `${fps} fps · worst ${Math.round(worst)}ms`;
      node.dataset.bad = fps < 50 || worst > 34 ? "yes" : "no";
      frames = 0;
      worst = 0;
      since = now;
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

async function boot() {
  root = page.querySelector("#beacon");
  const bar = page.querySelector("#boot .bar"),
    say = page.querySelector("#boot .say");
  let loaded = true;
  try {
    await fetchAll(beaconAssets(), (fraction, done, total) => {
      bar.style.transform = `scaleX(${fraction})`;
      say.textContent = `LOADING ${done}/${total}`;
    });
  } catch (cause) {
    // A prop that cannot make its noises is still a prop. Unlike the show, a
    // missing clip here costs the sound design and not the game, so the gate
    // reports it and stands aside rather than refusing to start.
    loaded = false;
    say.textContent = `NO AUDIO · ${cause.message}`;
  }
  // The casing comes off the gate's own copy, never off the remote URL: a
  // background-image built before the gate has run is an element load with no
  // `Origin`, and R2 answers those without the CORS header and caches the answer
  // for a year. `src()` hands back the blob, or the empty resource if there is
  // none — assets.js carries the whole reasoning.
  if (loaded) page.body.style.setProperty("--casing", `url("${src(beaconPack.art.casing)}")`);
  if (loaded) await decode(beaconPack.sfx);
  await new Promise((done) => setTimeout(done, loaded ? 260 : 1_400));
  page.body.classList.add("booted");

  // A round already under way wins over the mode in the URL: the device was put
  // down armed and picked up armed, and the clock is a timestamp so it is still
  // right. `?mode=` is for handing somebody a link to a fresh one.
  prefs = loadPrefs();
  const query = new URLSearchParams(location.search);
  if (query.get("fps")) meter();
  const asked = query.get("mode");
  const saved = load();
  state = saved ?? (asked === "bomb" || asked === "terminal" ? start(asked, Date.now(), prefs) : null);
  picked = Boolean(state);
  mount(state?.mode ?? "");
  // The chooser still needs a state for the loop to tick over, so it gets a
  // charge nobody has armed. `picked` is what stops it being shown.
  if (!state) state = start("bomb", Date.now(), prefs);
  step();
}

if (page?.querySelector("#beacon")) {
  page.addEventListener("pointerdown", press);
  page.addEventListener("pointerup", release);
  page.addEventListener("pointercancel", release);
  // A thumb that slides off the control is a thumb that let go. Without this a
  // player can arm a charge by pressing and then wandering off the button.
  page.addEventListener("pointerleave", release);
  // A locked phone drops the wake lock silently; a pocketed one comes back
  // without it. Re-take it rather than trusting the first request.
  page.addEventListener("visibilitychange", () => {
    if (!page.hidden && state) hold();
  });
  // Nothing on this surface is a link and nothing is selectable — a long press
  // on a prop should hold the charge, not offer to copy the word ARMED.
  page.addEventListener("contextmenu", (event) => event.preventDefault());
  // Pinch, in Safari's own vocabulary. `touch-action: none` stops the rest, but
  // WebKit's pinch arrives as `gesture*` events and is only refused here — and
  // a device zoomed to 150% mid-round is one nobody can read the clock on.
  for (const kind of ["gesturestart", "gesturechange", "gestureend"])
    page.addEventListener(kind, (event) => event.preventDefault());
  boot();
}
